import { useCallback, useEffect, useMemo } from "react";
import { Entity } from "@common/types/entity";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { getAccountingBasisLabel } from "@common/helpers/accountingBasis";
import { useHasAnyAccrualLedger, useLedgersByEntityIds } from "@api/ledger.api";
import { LedgerBasisOption } from "@components/ledger/LedgerBasisSelect";
import {
    getEntityIdsWithLedgerName,
    getMultiEntityBasisInfo,
    resolveMultiEntityLedger,
} from "./multiEntityLedger";

const ACCRUAL_UNAVAILABLE_VALUE = "accrual-unavailable";
const ACCRUAL_UNAVAILABLE_TOOLTIP =
    "Some selected entities have no accrual ledger.";

interface UseGeneralLedgerLedgerParams {
    selectedEntityIds: number[];
    // Stable set of all accessible business entity ids, so the currently
    // selected ledger's basis can be read even when it belongs to a
    // no-longer-selected entity (and the lookup query key never churns as the
    // selection changes).
    ledgerLookupEntityIds: number[];
    ledgerId: string | undefined;
    onLedgerIdResolved: (ledgerId: string) => void;
    // Owned by useAccountingTab so it stays live across report navigation.
    preferredBasis: AccountingLedgerBasis | undefined;
    onBasisResolved: (basis: AccountingLedgerBasis) => void;
}

interface UseGeneralLedgerLedgerResult {
    resolvedLedgerId: string | undefined;
    shouldRenderBasisSelect: boolean;
    basisOptions: LedgerBasisOption[];
    /** Under Accrual, disables entities that don't share the accrual ledger name; never disables under Cash. */
    isEntityDisabled: (entity: Entity) => boolean;
}

/**
 * Owns ledger selection for the General Ledger report in both single- and
 * multi-entity mode. A single `ledgerId` must reference a ledger that belongs
 * to one of the selected entities and whose name exists across all of them;
 * otherwise the report 404s ("Ledger not accessible") — which happens whenever
 * the selection changes while a `ledgerId` from a no-longer-selected entity is
 * still applied. This resolves a valid `ledgerId` synchronously from the loaded
 * ledgers (preserving the current basis when available, falling back to cash)
 * so the report never fires with a mismatched ledger, and exposes the basis
 * options shared across the selection. The GL route opts out of the shared
 * `useAccountingTab` reset effect so this is the sole owner.
 */
export function useGeneralLedgerLedger({
    selectedEntityIds,
    ledgerLookupEntityIds,
    ledgerId,
    onLedgerIdResolved,
    preferredBasis,
    onBasisResolved,
}: UseGeneralLedgerLedgerParams): UseGeneralLedgerLedgerResult {
    const { data: ledgersByEntityId } = useLedgersByEntityIds(
        ledgerLookupEntityIds,
    );

    const shouldRenderBasisSelect = useHasAnyAccrualLedger(
        ledgerLookupEntityIds,
    );

    const resolvedLedgerId = useMemo(() => {
        // Withhold the report until ledgers load rather than passing through a
        // possibly-stale ledgerId that would 404 ("Ledger not accessible").
        if (!ledgersByEntityId) {
            return undefined;
        }
        return resolveMultiEntityLedger({
            ledgersByEntityId,
            selectedEntityIds,
            currentLedgerId: ledgerId,
            preferredBasis,
        }).resolvedLedgerId;
    }, [ledgersByEntityId, selectedEntityIds, ledgerId, preferredBasis]);

    const { basisOptions, enabledEntityIds } = useMemo<{
        basisOptions: LedgerBasisOption[];
        enabledEntityIds: number[] | undefined;
    }>(() => {
        if (!ledgersByEntityId) {
            return { basisOptions: [], enabledEntityIds: undefined };
        }

        const basisInfo = getMultiEntityBasisInfo({
            ledgersByEntityId,
            selectedEntityIds,
        });
        const resolvedLedger = Object.values(ledgersByEntityId)
            .flat()
            .find((ledger) => ledger.id === resolvedLedgerId);

        // Alias the resolved id onto the option of its own basis so the select's
        // value always matches one of its options.
        const cashValue =
            resolvedLedger?.basis === AccountingLedgerBasis.cash
                ? resolvedLedgerId
                : basisInfo.cashLedgerId;
        const accrualValue =
            resolvedLedger?.basis === AccountingLedgerBasis.accruals
                ? resolvedLedgerId
                : basisInfo.accrualLedgerId;

        const options: LedgerBasisOption[] = [];
        if (cashValue) {
            options.push({
                value: cashValue,
                label: getAccountingBasisLabel(AccountingLedgerBasis.cash),
            });
        }
        if (accrualValue) {
            options.push({
                value: accrualValue,
                label: getAccountingBasisLabel(AccountingLedgerBasis.accruals),
            });
        } else {
            options.push({
                value: ACCRUAL_UNAVAILABLE_VALUE,
                label: getAccountingBasisLabel(AccountingLedgerBasis.accruals),
                disabled: true,
                tooltip: ACCRUAL_UNAVAILABLE_TOOLTIP,
            });
        }

        const gatedEntityIds =
            resolvedLedger?.basis === AccountingLedgerBasis.accruals &&
            resolvedLedger.name
                ? getEntityIdsWithLedgerName({
                      ledgersByEntityId,
                      entityIds: ledgerLookupEntityIds,
                      ledgerName: resolvedLedger.name,
                  })
                : undefined;

        return { basisOptions: options, enabledEntityIds: gatedEntityIds };
    }, [
        ledgersByEntityId,
        selectedEntityIds,
        resolvedLedgerId,
        ledgerLookupEntityIds,
    ]);

    const isEntityDisabled = useCallback(
        (entity: Entity) =>
            enabledEntityIds !== undefined &&
            !enabledEntityIds.includes(entity.id),
        [enabledEntityIds],
    );

    useEffect(() => {
        if (!resolvedLedgerId) {
            return;
        }
        // Persist the displayed basis so it carries across reports; a Cash fallback becomes the new preference.
        const resolvedLedger = ledgersByEntityId
            ? Object.values(ledgersByEntityId)
                  .flat()
                  .find((ledger) => ledger.id === resolvedLedgerId)
            : undefined;
        if (resolvedLedger && resolvedLedger.basis !== preferredBasis) {
            onBasisResolved(resolvedLedger.basis);
        }
        if (resolvedLedgerId !== ledgerId) {
            onLedgerIdResolved(resolvedLedgerId);
        }
    }, [
        resolvedLedgerId,
        ledgerId,
        onLedgerIdResolved,
        ledgersByEntityId,
        onBasisResolved,
        preferredBasis,
    ]);

    return {
        resolvedLedgerId,
        shouldRenderBasisSelect,
        basisOptions,
        isEntityDisabled,
    };
}
